import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { API, APP_NAME } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";

    const [formData, setFormData] = useState({
        new_password: "",
        confirm_password: "",
    });
    const [showPasswords, setShowPasswords] = useState({
        new: false,
        confirm: false,
    });
    const [loading, setLoading] = useState(false);

    // Extract token from query params
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");

    // Update page title
    useEffect(() => {
        document.title = `Reset Password - ${APP_NAME}`;
        if (!token) {
            toast.error("Valid reset token is missing.");
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!token) {
            toast.error("Reset token is missing.");
            return;
        }

        if (formData.new_password !== formData.confirm_password) {
            toast.error("Passwords do not match.");
            return;
        }

        if (formData.new_password.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }

        setLoading(true);

        try {
            // Using /auth/reset-password for consistency with /auth/forgot-password
            // but if your API is specifically just /reset-password, remove the /auth prefix
            await axios.post(`${API}/auth/reset-password`, {
                token,
                new_password: formData.new_password,
            });
            toast.success("Password reset successful! You can now login.");
            navigate("/login");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to reset password. The link may be expired.");
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4 cursor-pointer" onClick={() => navigate("/")}>
                        <img src={logoUrl} alt="Logo" className="w-14 h-14 rounded-full" />
                        <span className="text-2xl font-semibold text-[#d4af37]">BackOffice</span>
                    </div>
                    <h1 className="text-3xl font-bold text-[#f8f8f8] mb-2">Reset Password</h1>
                    <p className="text-[#f8f8f8]/70">Enter your new password below</p>
                </div>

                <div className="bg-[#0a1a1f]/80 backdrop-blur-sm rounded-2xl border border-[#d4af37]/20 p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <Label htmlFor="new_password" className="text-[#f8f8f8]">New Password</Label>
                            <div className="relative mt-2">
                                <Input
                                    id="new_password"
                                    type={showPasswords.new ? "text" : "password"}
                                    value={formData.new_password}
                                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                                    placeholder="••••••••"
                                    required
                                    className="bg-[#005f6b]/20 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:outline-none focus:ring-0 pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility("new")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#f8f8f8]/60 hover:text-[#00ced1] transition-colors"
                                >
                                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="confirm_password" className="text-[#f8f8f8]">Confirm New Password</Label>
                            <div className="relative mt-2">
                                <Input
                                    id="confirm_password"
                                    type={showPasswords.confirm ? "text" : "password"}
                                    value={formData.confirm_password}
                                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                                    placeholder="••••••••"
                                    required
                                    className="bg-[#005f6b]/20 border-[#005f6b] text-[#f8f8f8] placeholder:text-[#f8f8f8]/40 focus:border-[#00ced1] focus:outline-none focus:ring-0 pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility("confirm")}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#f8f8f8]/60 hover:text-[#00ced1] transition-colors"
                                >
                                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading || !token}
                            className="w-full bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl text-base"
                        >
                            {loading ? "Resetting..." : "Reset Password"}
                        </Button>
                    </form>

                    {!token && (
                        <div className="mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm text-center">
                            Invalid or missing reset token. Please request a new link.
                        </div>
                    )}

                    <div className="mt-6 text-center">
                        <Link to="/login" className="text-[#00ced1] hover:text-[#d4af37] font-medium">
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
